import { Severity } from "@velaro/velaro-component-lib";
import ToastAlertContext from "@/context/ToastAlertContext";
import { ToastAlertSettings } from "../types";
import React, { useCallback, useEffect, useRef, useState } from "react";
import useApi from "@/hooks/useApi";
import { createRoot } from "react-dom/client";
import AppLoading from "@/components/AppLoading";

// Inline-styled toast — no Tailwind class dependency, always readable.
const TOAST_STYLES: Record<number, { bg: string; border: string; icon: string; iconColor: string }> = {
  [Severity.Success]: { bg: "#f0fdf4", border: "#16a34a", icon: "✓", iconColor: "#16a34a" },
  [Severity.Info]:    { bg: "#eff6ff", border: "#1361C8", icon: "ℹ", iconColor: "#1361C8" },
  [Severity.Warning]: { bg: "#fffbeb", border: "#d97706", icon: "⚠", iconColor: "#d97706" },
  [Severity.Error]:   { bg: "#fef2f2", border: "#dc2626", icon: "✕", iconColor: "#dc2626" }
};

function VelaroToast({ severity, message, id }: { severity: Severity; message: string; id: string }) {
  const [visible, setVisible] = useState(true);
  const s = TOAST_STYLES[severity];

  useEffect(() => {
    const t = setTimeout(() => setVisible(false), 4000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!visible) {
      setTimeout(() => {
        const el = document.getElementById(id);
        el?.remove();
      }, 200);
    }
  }, [visible, id]);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: "10px",
        background: s.bg,
        border: `1px solid ${s.border}`,
        borderLeft: `4px solid ${s.border}`,
        borderRadius: "8px",
        padding: "12px 14px",
        width: "320px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        pointerEvents: "auto",
        opacity: visible ? 1 : 0,
        transition: "opacity 0.2s ease",
        fontFamily: "Rubik, ui-sans-serif, system-ui, sans-serif",
        fontSize: "14px",
        lineHeight: "1.5",
        color: "#1e293b",
        boxSizing: "border-box"
      }}
    >
      <span style={{ fontSize: "16px", color: s.iconColor, flexShrink: 0, marginTop: "1px", fontWeight: 700 }}>
        {s.icon}
      </span>
      <span style={{ flex: 1, color: "#1e293b" }}>{message}</span>
      <button
        onClick={() => setVisible(false)}
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          color: "#64748b",
          fontSize: "16px",
          lineHeight: 1,
          padding: "0 0 0 4px",
          flexShrink: 0
        }}
        aria-label="Dismiss"
      >
        ×
      </button>
    </div>
  );
}

export default function ToastAlertProvider(props: {
  children: React.ReactNode;
}) {
  const [toastSettings, setToastSettings] = useState<ToastAlertSettings>({
    stickyAlerts: false,
    stickyDuration: 5000
  });
  const [loading, setLoading] = useState(true);
  const api = useApi();

  useEffect(() => {
    async function fetchSettings() {
      try {
        const resp = await api.messaging.get("NotificationSettings");
        if (resp.ok) {
          setToastSettings(await resp.json());
        }
      } catch {
        // Use defaults if settings unreachable
      } finally {
        setLoading(false);
      }
    }

    fetchSettings();
  }, [api.messaging]);

  const alertsContainer = useRef<HTMLDivElement>(null);

  const displayToast = useCallback(
    (severity: Severity, message: string) => {
      const r = (Math.random() + 1).toString(36).substring(7);
      const newElement = document.createElement("div");
      newElement.id = r;
      const container = alertsContainer.current;
      if (container === null) {
        return;
      }
      container.appendChild(newElement);
      const root = createRoot(newElement);
      root.render(
        <VelaroToast severity={severity} message={message} id={r} />
      );
    },
    []
  );

  if (loading) {
    return <AppLoading />;
  }

  return (
    <ToastAlertContext.Provider
      value={{ displayToast, toastSettings, loading }}
    >
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        style={{
          position: "fixed",
          top: "16px",
          right: "16px",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          gap: "8px",
          pointerEvents: "none",
          zIndex: 9999
        }}
        ref={alertsContainer}
      ></div>
      {props.children}
    </ToastAlertContext.Provider>
  );
}
